import { services } from '@config/services';
import type {
  PluginConnectionsResponse,
  PluginConnectionRow,
  VersionDistributionItem,
  VersionDistributionResponse,
} from '../types';
import { integrationApi } from './_base';

/**
 * Version Management tab endpoints. Both reads live on the integration service's
 * plugin resource:
 *
 *   GET /v1/plugin/{id}/version?includeDistribution=true&publishedOnly=true
 *     → { pluginVersions: [...], totalEnabledMerchants, totalDisabledMerchants }
 *   GET /v1/plugin/{id}/merchantPlugins?pluginVersionId&search&page&pageSize
 *     → { items: [...], total }
 *
 * The version read is an ADDITIVE extension of the existing `.../version`
 * endpoint (auth `Plugins.Get`); the distribution fields ride the opt-in query
 * params. The connections read is a separate action (auth
 * `Plugins.ManageConnections`).
 */

export interface VersionDistributionArgs {
  pluginId: string;
}

export interface PluginMerchantPluginsArgs {
  pluginId: string;
  /** Restrict to connections bound to this version. Omit to search across all. */
  pluginVersionId?: string;
  /** Exact merchant id, or a merchant-name substring (merchant-service search). */
  search?: string;
  mappingReviewPending?: boolean;
  page?: number;
  pageSize?: number;
}

/** Serialize a param map to a query string, dropping empty/undefined values. */
function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const versionManagementRequestBuilders = {
  getVersionDistribution: ({ pluginId }: VersionDistributionArgs) => ({
    url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/version${toQuery({
      includeDistribution: true,
      publishedOnly: true,
    })}`,
    method: 'GET',
  }),
  getPluginMerchantPlugins: ({
    pluginId,
    pluginVersionId,
    search,
    mappingReviewPending,
    page,
    pageSize,
  }: PluginMerchantPluginsArgs) => ({
    url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/merchantPlugins${toQuery({
      pluginVersionId,
      search,
      mappingReviewPending,
      page,
      pageSize,
    })}`,
    method: 'GET',
  }),
};

/**
 * Normalize the version-distribution envelope. The service returns
 * `{ pluginVersions, totalEnabledMerchants?, totalDisabledMerchants? }`; fail
 * open to an empty version list so a shape shift can't crash the table, and
 * leave the totals undefined when absent (they render as a dash, not zero).
 */
export function transformVersionDistributionResponse(
  response: VersionDistributionResponse | { pluginVersions?: VersionDistributionItem[] },
): VersionDistributionResponse {
  const pluginVersions = Array.isArray(response?.pluginVersions) ? response.pluginVersions : [];
  const totals = response as VersionDistributionResponse;
  return {
    pluginVersions,
    totalEnabledMerchants: totals.totalEnabledMerchants,
    totalDisabledMerchants: totals.totalDisabledMerchants,
  };
}

/**
 * Normalize the connections envelope. The service returns `{ items, total }`;
 * fail open to an empty page, but treat a missing/non-numeric `total` as an
 * invalid response. `total` drives pagination — deriving it from the current
 * page's row count would report a single page and hide reachable connections,
 * so surface the retryable error state instead.
 */
export function transformPluginConnectionsResponse(
  response: PluginConnectionsResponse | { items?: PluginConnectionRow[]; total?: number },
): PluginConnectionsResponse {
  const items = Array.isArray(response?.items) ? response.items : [];
  if (typeof response?.total !== 'number') {
    throw new Error('Plugin connections response is missing total');
  }
  return { items, total: response.total };
}

const versionManagementApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Published versions enriched with per-version connection distribution. */
    getVersionDistribution: builder.query<VersionDistributionResponse, VersionDistributionArgs>({
      query: (args) => versionManagementRequestBuilders.getVersionDistribution(args),
      transformResponse: transformVersionDistributionResponse,
      providesTags: (_r, _e, { pluginId }) => [{ type: 'VersionDistribution', id: pluginId }],
    }),
    /** A page of a plugin's merchant connections, optionally scoped to a version. */
    getPluginMerchantPlugins: builder.query<PluginConnectionsResponse, PluginMerchantPluginsArgs>({
      query: (args) => versionManagementRequestBuilders.getPluginMerchantPlugins(args),
      transformResponse: transformPluginConnectionsResponse,
      providesTags: (_r, _e, { pluginId }) => [{ type: 'PluginConnections', id: pluginId }],
    }),
  }),
});

export const {
  useGetVersionDistributionQuery,
  useGetPluginMerchantPluginsQuery,
  useLazyGetPluginMerchantPluginsQuery,
} = versionManagementApi;
